import { inject } from '@angular/core'
import {
    HttpInterceptorFn,
    HttpErrorResponse,
    HttpHeaders,
    HttpRequest,
} from '@angular/common/http'
import { tap } from 'rxjs/operators'
import { AppConstants } from '@app/core/constants/app.constants'
import { API_ERROR_CODES } from '../constants/api-errors.constants'
import { environment } from 'src/environments/environment'
import { authEndpoints, permissionEndpoints } from '../constants/valueConstants'
import { EMAIL_COMPOSER_ZOHO_CONSTANTS } from '@app/shared/constants/email-composer-zoho.constants'
import { PermissionService } from '@core/services/permission-service/permission-service'
import { IndexDBHelper } from '@core/services/indexdb-service/indexdb-helper.service'

/** Gateway ABAC (NAV-12636) reads this; Express lower-cases it to `x-app-id`. */
export const APP_ID_HEADER = 'x-app-id'

const BOOTSTRAP_PATHS = [
    authEndpoints.USER_INFO_PATH,
    authEndpoints.SESSION_ACTIVITY_PATH,
    permissionEndpoints.GET_PERMISSIONS,
]

function isBootstrapRequest(url: string): boolean {
    return BOOTSTRAP_PATHS.some((path) => url.includes(path))
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null
}

function isZohoDraftLinkRequired(error: HttpErrorResponse, requestUrl: string): boolean {
    const isZohoDraftRequest = EMAIL_COMPOSER_ZOHO_CONSTANTS.API.DRAFT_REQUEST_URL_SEGMENTS.every(
        (segment) => requestUrl.includes(segment),
    )
    if (!isZohoDraftRequest) return false
    if (!isPlainRecord(error.error)) return false
    return error.error['errorCode'] === EMAIL_COMPOSER_ZOHO_CONSTANTS.API.ERROR_CODE_LINK_REQUIRED
}

function gatewayBaseUrls(): string[] {
    return [environment.apiBaseUrl, environment.apiUrl]
        .filter((value): value is string => typeof value === 'string' && value.length > 0)
        .map((value) => value.replace(/\/$/, ''))
}

/** Navigator gateway only — do not attach app id to SSO or third-party calls. */
export function isNavigatorGatewayRequest(url: string): boolean {
    if (url.startsWith('/api/')) {
        return true
    }
    return gatewayBaseUrls().some((base) => url === base || url.startsWith(`${base}/`))
}

function withAppId(headers: HttpHeaders, req: HttpRequest<unknown>): HttpHeaders {
    if (!isNavigatorGatewayRequest(req.url) || headers.has(APP_ID_HEADER)) {
        return headers
    }
    return headers.set(APP_ID_HEADER, AppConstants.SUB_APP_ID)
}

/**
 * Auth is carried by cookies on same-origin / credentialed CORS requests via
 * `withCredentials: true`. We intentionally do not copy tokens from
 * `document.cookie` into `Authorization: Bearer`, because readable cookies are
 * not HttpOnly and duplicating the secret in a header widens XSS impact; when
 * cookies are HttpOnly, that copy would be a no-op anyway.
 * Call sites may still set `Authorization` explicitly when required.
 * Finance ABAC (and any other gateway group that is on) also needs `x-app-id: 6`.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
    const clonedReq = req.clone({
        url: req.url,
        withCredentials: true,
        headers: withAppId(req.headers, req),
    })
    const permissionService = inject(PermissionService)
    const indexDBHelper = inject(IndexDBHelper)

    return next(clonedReq).pipe(
        tap({
            next: () => undefined,
            error: (error: HttpErrorResponse) => {
                if (
                    error.status === API_ERROR_CODES.UNAUTHORIZED ||
                    error.status === API_ERROR_CODES.FORBIDDEN
                ) {
                    if (isBootstrapRequest(req.url)) {
                        return
                    }
                    // Zoho draft first-attempt can intentionally return 403 LINK_REQUIRED; let feature flow handle OAuth popup.
                    if (isZohoDraftLinkRequired(error, req.url)) {
                        return
                    }
                    // Prevent stale permissions from one user affecting the next login flow.
                    void (async () => {
                        permissionService.clearCache()
                        try {
                            await indexDBHelper.clearPermissions()
                        } catch (clearError) {
                            if (!environment.production) {
                                console.warn(
                                    'Failed to clear persisted permissions before SSO redirect',
                                    clearError,
                                )
                            }
                        }
                        const nextUrl = window.location.href
                        window.location.href = `${environment.ssoBaseUrl}${authEndpoints.SIGNIN_PATH}?next=${encodeURIComponent(nextUrl)}`
                    })()
                }
            },
        }),
    )
}
